import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getMarketingOverview,
  getMarketingFunnel,
  getMarketingChannels,
  getMarketingCampaigns,
  getMarketingCoupons,
  getMarketingTrends,
} from '../../api/client';
import {
  TrendingUp,
  Users,
  DollarSign,
  ShoppingCart,
  MousePointer,
  BarChart3,
  Filter,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

interface KpiData {
  revenue: number;
  total_payments: number;
  leads: number;
  enrollments: number;
  visitors: number;
  purchases: number;
}

interface FunnelStep {
  step: string;
  count: number;
  conversion_rate: number;
}

interface Channel {
  source: string;
  medium: string;
  campaign: string;
  sessions: number;
  leads: number;
  purchases: number;
  revenue: number;
  avg_order_value: number;
  conversion_rate: number;
}

interface Campaign {
  campaign: string;
  source: string;
  purchases: number;
  revenue: number;
  sessions: number;
}

interface CouponStat {
  coupon_code: string;
  uses: number;
  revenue: number;
  avg_order_value: number;
}

interface TrendPoint {
  date: string;
  revenue?: number;
  purchases?: number;
  leads?: number;
  visitors?: number;
}

const DATE_RANGES = [
  { label: 'Last 7 Days', value: '7' },
  { label: 'Last 30 Days', value: '30' },
  { label: 'This Month', value: 'month' },
  { label: 'Last 3 Months', value: '90' },
];

function getDateRange(range: string): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  if (range === 'month') {
    start.setDate(1);
  } else {
    start.setDate(end.getDate() - parseInt(range));
  }
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

function formatCurrency(val: number): string {
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
  return `₹${val.toFixed(0)}`;
}

function formatNumber(val: number): string {
  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
  return String(val);
}

const STEP_NAMES: Record<string, string> = {
  visitors: 'Visitors',
  leads: 'Leads',
  enrollments: 'Enrollments',
  checkouts: 'Checkouts',
  purchases: 'Purchases',
};

const STEP_COLORS = ['bg-blue-500', 'bg-purple-500', 'bg-amber-500', 'bg-rose-500', 'bg-emerald-500'];

export default function AdminMarketing(): React.JSX.Element {
  const [range, setRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState<KpiData | null>(null);
  const [funnel, setFunnel] = useState<FunnelStep[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [coupons, setCoupons] = useState<CouponStat[]>([]);
  const [trends, setTrends] = useState<{ revenue: TrendPoint[]; leads: TrendPoint[]; visitors: TrendPoint[] }>({
    revenue: [],
    leads: [],
    visitors: [],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { start, end } = getDateRange(range);
    const params = { start, end };

    try {
      const [overviewRes, funnelRes, channelsRes, campaignsRes, couponsRes, trendsRes] = await Promise.all([
        getMarketingOverview(params),
        getMarketingFunnel(params),
        getMarketingChannels(params),
        getMarketingCampaigns(params),
        getMarketingCoupons(params),
        getMarketingTrends(params),
      ]);

      setKpi(overviewRes.data ?? null);
      setFunnel(funnelRes.data?.funnel ?? []);
      setChannels(channelsRes.data?.channels ?? []);
      setCampaigns(campaignsRes.data?.campaigns ?? []);
      setCoupons(couponsRes.data?.coupons ?? []);
      setTrends({
        revenue: trendsRes.data?.revenue ?? [],
        leads: trendsRes.data?.leads ?? [],
        visitors: trendsRes.data?.visitors ?? [],
      });
    } catch (err: any) {
      console.error('Marketing data load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kpiCards = [
    { label: 'Revenue', value: kpi?.revenue ?? 0, icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Visitors', value: kpi?.visitors ?? 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Leads', value: kpi?.leads ?? 0, icon: MousePointer, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Purchases', value: kpi?.purchases ?? 0, icon: ShoppingCart, color: 'text-amber-600', bg: 'bg-amber-50' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Marketing Analytics</h1>
            <p className="text-sm text-slate-500">Track ad performance, funnel conversion, and revenue attribution</p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none"
            >
              {DATE_RANGES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((card, i) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center`}>
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <TrendingUp className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {card.label === 'Revenue' ? formatCurrency(card.value) : formatNumber(card.value)}
              </p>
              <p className="text-xs text-slate-500 mt-1">{card.label}</p>
            </motion.div>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            <p className="text-sm text-slate-500 ml-2">Loading analytics...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Funnel Visualization */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <BarChart3 className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-bold text-slate-900">Conversion Funnel</h2>
              </div>
              <div className="space-y-4">
                {funnel.map((step, i) => {
                  const maxCount = Math.max(...funnel.map((s) => s.count), 1);
                  const widthPercent = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                  return (
                    <div key={step.step} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-slate-700">{STEP_NAMES[step.step] ?? step.step}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-slate-900">{formatNumber(step.count)}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            step.conversion_rate >= 50 ? 'bg-green-50 text-green-600' :
                            step.conversion_rate >= 20 ? 'bg-amber-50 text-amber-600' :
                            'bg-rose-50 text-rose-600'
                          }`}>
                            {step.conversion_rate}%
                          </span>
                        </div>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${widthPercent}%` }}
                          transition={{ duration: 0.8, delay: i * 0.1 }}
                          className={`h-full rounded-full ${STEP_COLORS[i % STEP_COLORS.length]}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Channel Breakdown */}
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
              <div className="p-5 border-b border-slate-100">
                <h2 className="text-lg font-bold text-slate-900">Channel Performance</h2>
                <p className="text-xs text-slate-500">Revenue attribution by traffic source</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="text-left text-xs font-bold text-slate-500 uppercase px-5 py-3">Source / Medium</th>
                      <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Sessions</th>
                      <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Leads</th>
                      <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Purchases</th>
                      <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Revenue</th>
                      <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">AOV</th>
                      <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Conv</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {channels.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-5 py-8 text-center text-sm text-slate-400">No channel data available</td>
                      </tr>
                    ) : (
                      channels.map((ch) => (
                        <tr key={`${ch.source}-${ch.medium}-${ch.campaign}`} className="hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{ch.source}</p>
                              <p className="text-xs text-slate-500">{ch.medium} / {ch.campaign}</p>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-right text-sm text-slate-600">{formatNumber(ch.sessions)}</td>
                          <td className="px-5 py-3 text-right text-sm text-slate-600">{formatNumber(ch.leads)}</td>
                          <td className="px-5 py-3 text-right text-sm text-slate-600">{formatNumber(ch.purchases)}</td>
                          <td className="px-5 py-3 text-right text-sm font-bold text-emerald-600">{formatCurrency(ch.revenue)}</td>
                          <td className="px-5 py-3 text-right text-sm text-slate-600">{formatCurrency(ch.avg_order_value)}</td>
                          <td className="px-5 py-3 text-right">
                            <span className="text-xs font-bold text-amber-600">{ch.conversion_rate}%</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Campaigns + Coupons side by side */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Campaigns */}
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                <div className="p-5 border-b border-slate-100">
                  <h2 className="text-lg font-bold text-slate-900">Top Campaigns</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left text-xs font-bold text-slate-500 uppercase px-5 py-3">Campaign</th>
                        <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Source</th>
                        <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Sales</th>
                        <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {campaigns.length === 0 ? (
                        <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-slate-400">No campaign data</td></tr>
                      ) : (
                        campaigns.map((c) => (
                          <tr key={`${c.campaign}-${c.source}`} className="hover:bg-slate-50 transition-colors">
                            <td className="px-5 py-3 text-sm font-semibold text-slate-900">{c.campaign}</td>
                            <td className="px-5 py-3 text-right text-sm text-slate-600">{c.source}</td>
                            <td className="px-5 py-3 text-right text-sm text-slate-600">{c.purchases}</td>
                            <td className="px-5 py-3 text-right text-sm font-bold text-emerald-600">{formatCurrency(c.revenue)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Coupons */}
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                <div className="p-5 border-b border-slate-100">
                  <h2 className="text-lg font-bold text-slate-900">Coupon Performance</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left text-xs font-bold text-slate-500 uppercase px-5 py-3">Code</th>
                        <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Uses</th>
                        <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Revenue</th>
                        <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">AOV</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {coupons.length === 0 ? (
                        <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-slate-400">No coupon usage</td></tr>
                      ) : (
                        coupons.map((c) => (
                          <tr key={c.coupon_code} className="hover:bg-slate-50 transition-colors">
                            <td className="px-5 py-3">
                              <span className="px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-bold">{c.coupon_code}</span>
                            </td>
                            <td className="px-5 py-3 text-right text-sm text-slate-600">{c.uses}</td>
                            <td className="px-5 py-3 text-right text-sm font-bold text-emerald-600">{formatCurrency(c.revenue)}</td>
                            <td className="px-5 py-3 text-right text-sm text-slate-600">{formatCurrency(c.avg_order_value)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Daily Trend Chart */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-bold text-slate-900">Daily Trends</h2>
              </div>
              <TrendChart data={trends} />
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function TrendChart({ data }: { data: { revenue: TrendPoint[]; leads: TrendPoint[]; visitors: TrendPoint[] } }) {
  const { revenue, leads, visitors } = data;
  if (!revenue.length && !leads.length && !visitors.length) {
    return <p className="text-sm text-slate-400 text-center py-8">No trend data available</p>;
  }

  // Simple SVG bar chart
  const allDates = Array.from(new Set([
    ...revenue.map((d) => d.date),
    ...leads.map((d) => d.date),
    ...visitors.map((d) => d.date),
  ])).sort();

  const maxRevenue = Math.max(...revenue.map((d) => d.revenue ?? 0), 1);
  const chartHeight = 160;
  const barWidth = Math.max(8, Math.min(32, 600 / allDates.length));

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        <svg viewBox={`0 0 ${allDates.length * (barWidth + 4)} ${chartHeight + 40}`} className="w-full">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
            <line
              key={pct}
              x1={0}
              y1={chartHeight * (1 - pct)}
              x2={allDates.length * (barWidth + 4)}
              y2={chartHeight * (1 - pct)}
              stroke="#e2e8f0"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
          ))}

          {allDates.map((date, i) => {
            const rev = revenue.find((d) => d.date === date)?.revenue ?? 0;
            const barHeight = (rev / maxRevenue) * chartHeight;
            const x = i * (barWidth + 4);

            return (
              <g key={date}>
                <rect
                  x={x}
                  y={chartHeight - barHeight}
                  width={barWidth}
                  height={barHeight}
                  rx={4}
                  fill="#F5A623"
                  opacity={0.8}
                />
                <text
                  x={x + barWidth / 2}
                  y={chartHeight + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#64748b"
                >
                  {new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="flex items-center justify-center gap-6 mt-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-amber-500" />
            <span className="text-xs text-slate-500">Revenue</span>
          </div>
        </div>
      </div>
    </div>
  );
}
